/**
 * 顧客系の型と入力検証（02 5.3.3 company・5.3.4 sole-proprietor・5.3.5 customer-contact）。
 * 画面（React Hook Form の zodResolver）と API（ZodValidationPipe）で同じスキーマを使う（02 1.5・03 3.6.1）。
 * - 任意項目の空文字は null（02 2.10）。null を送ると値を消す（更新）
 * - 本文に定義外のプロパティがあれば 400（D-019：strict）
 */
import { z } from 'zod';
import { BANK_ACCOUNT_TYPE_VALUES } from '../enums/bank-account-type';
import { CUSTOMER_TYPE_VALUES } from '../enums/customer-type';
import { TAG_CODE_VALUES } from '../enums/tag-code';
import { TRADE_PARTNER_TYPE_VALUES } from '../enums/trade-partner-type';
import { TRADE_TYPE_VALUES } from '../enums/trade-type';
import {
  zAccountNumber,
  zBankCode,
  zBranchCode,
  zCode,
  zCorporateNumber,
  zEmail,
  zEnum,
  zKatakana,
  zMyNumber,
  zPhone,
  zPostalCode,
  zRequiredText,
  zShortText,
  zSystemCode,
  zUrl,
  zVersion,
  zNullable,
} from './common.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

const FULL_WIDTH_ALPHA_PATTERN = /[Ａ-Ｚａ-ｚ]/;

/** 空文字を null にする任意項目の部品は common.schema に置いた（⑥：公開フォームの別チャンクが顧客の定義を読み込まないように）。ここからも使える */
export { zNullable } from './common.schema';

/**
 * インボイス番号の入力の整え直し（243 UT-767）：全角の英数字を半角に、英字を大文字にする。
 * 't1234567890123'・'Ｔ１２３４５６７８９０１２３' → 'T1234567890123'
 */
export function sanitizeInvoiceInput(value: string): string {
  return value.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0)).toUpperCase();
}

/**
 * インボイス番号（サーバー・取込で使う）：全角英字が 1 文字でもあれば MSG-V-074 で止める（247 UT-701：画面のチェックをすり抜けた値もサーバーで止める）。
 * 全角英字が無ければ整え直し（全角数字→半角、小文字→大文字）の後に「T＋13 桁」かを見て、違えば MSG-V-016（03 SCR-008 No.6）。
 * 全角英字は整え直しより先に見る（243 で整え直しを先にしたため 'Ｔ1234567890123' が 200 で保存されていた）
 */
export const zInvoiceNumberInput = z
  .string({ message: 'MSG-V-016' })
  .refine((value) => !FULL_WIDTH_ALPHA_PATTERN.test(value), 'MSG-V-074')
  .transform((value) => sanitizeInvoiceInput(value.trim()))
  .refine((value) => /^T\d{13}$/.test(value), 'MSG-V-016');

/**
 * インボイス番号（画面の入力欄で使う）：入力を整え直してから「T＋13 桁」かを見る（243 UT-767）。
 * 全角・小文字で入れても通し、送る前に sanitizeInvoiceInput で半角大文字に直す（送る値に全角英字は残らない）
 */
export const zInvoiceNumberFormInput = z
  .string({ message: 'MSG-V-016' })
  .transform((value) => sanitizeInvoiceInput(value.trim()))
  .refine((value) => /^T\d{13}$/.test(value), 'MSG-V-016');

/** インボイス番号の入力チェック（画面用。UT-767）：空は任意のためエラーなし。誤りはメッセージ ID、正しければ null */
export function validateInvoiceNumber(value: string): string | null {
  if (value.trim() === '') {
    return null;
  }
  const result = zInvoiceNumberFormInput.safeParse(value);
  return result.success ? null : (result.error.issues[0]?.message ?? 'MSG-V-016');
}

function uniqueArray<T extends z.ZodTypeAny>(item: T, max: number) {
  return z
    .array(item, { message: 'MSG-V-020' })
    .max(max, 'MSG-V-020')
    .refine((values) => new Set(values).size === values.length, 'MSG-V-020');
}

export const zTradePartnerTypes = uniqueArray(zEnum(TRADE_PARTNER_TYPE_VALUES), 2);
export const zTradeTypes = uniqueArray(zEnum(TRADE_TYPE_VALUES), 10);

/** 企業・個人事業主に共通の登録・更新項目（CustomerCommon の登録列。名前・カナは種別ごとに別） */
const CUSTOMER_COMMON_INPUT_SHAPE = {
  postal_code: zNullable(zPostalCode).optional(),
  address: zShortText(500).optional(),
  invoice_number: zNullable(zInvoiceNumberInput).optional(),
  sales_user_code: zNullable(zSystemCode).optional(),
  tag_code: zNullable(zEnum(TAG_CODE_VALUES)).optional(),
  bank_name: zShortText(100).optional(),
  branch_name: zShortText(100).optional(),
  bank_code: zNullable(zBankCode).optional(),
  branch_code: zNullable(zBranchCode).optional(),
  bank_account_type: zNullable(zEnum(BANK_ACCOUNT_TYPE_VALUES)).optional(),
  account_number: zNullable(zAccountNumber).optional(),
  account_holder_name: zShortText(100).optional(),
  account_holder_name_phonetic: zShortText(100).optional(),
};

/** 企業の登録・更新で主担当を一緒に書くときの入れ子（02 PrimaryContactInput・D-034） */
export const PrimaryContactInputSchema = z
  .object({
    last_name: zRequiredText(50, 'MSG-V-078'),
    first_name: zRequiredText(50, 'MSG-V-079'),
    job_title: zShortText(100).optional(),
    department_1: zShortText(100).optional(),
    department_2: zShortText(100).optional(),
    phone_number: zNullable(zPhone).optional(),
    email: zNullable(zEmail).optional(),
  })
  .strict();

const COMPANY_INPUT_SHAPE = {
  customer_name: zRequiredText(200, 'MSG-V-053'),
  customer_name_phonetic: zKatakana(200, 'MSG-V-052'),
  ...CUSTOMER_COMMON_INPUT_SHAPE,
  representative_name: zShortText(100).optional(),
  website_url: zNullable(zUrl).optional(),
  corporate_number: zNullable(zCorporateNumber).optional(),
  primary_customer_contact: PrimaryContactInputSchema.optional(),
};

/** API-COMP-04 企業の登録 */
export const CompanyCreateSchema = z
  .object({
    ...COMPANY_INPUT_SHAPE,
    trade_partner_type: zTradePartnerTypes.default([]),
    trade_type: zTradeTypes.default([]),
  })
  .strict();

/** API-COMP-06 企業の更新（すべて任意＋version。customer_type は受け付けない） */
export const CompanyUpdateSchema = z
  .object({
    ...COMPANY_INPUT_SHAPE,
    customer_name: COMPANY_INPUT_SHAPE.customer_name.optional(),
    customer_name_phonetic: COMPANY_INPUT_SHAPE.customer_name_phonetic.optional(),
    trade_partner_type: zTradePartnerTypes.optional(),
    trade_type: zTradeTypes.optional(),
    version: zVersion,
  })
  .strict();

/** マイナンバー：省略・空は「変更しない」（02 5.3.4・ブリーフ C-07） */
const zMyNumberInput = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  zMyNumber.optional(),
);

const SOLE_PROPRIETOR_INPUT_SHAPE = {
  customer_name: zRequiredText(200, 'MSG-V-066'),
  customer_name_phonetic: zRequiredText(200, 'MSG-V-065'),
  ...CUSTOMER_COMMON_INPUT_SHAPE,
  phone_number: zNullable(zPhone).optional(),
  email: zNullable(zEmail).optional(),
  my_number: zMyNumberInput,
};

/** API-SOLE-02 個人事業主の登録 */
export const SoleProprietorCreateSchema = z
  .object({ ...SOLE_PROPRIETOR_INPUT_SHAPE, trade_partner_type: zTradePartnerTypes.default([]) })
  .strict();

/** API-SOLE-04 個人事業主の更新 */
export const SoleProprietorUpdateSchema = z
  .object({
    ...SOLE_PROPRIETOR_INPUT_SHAPE,
    customer_name: SOLE_PROPRIETOR_INPUT_SHAPE.customer_name.optional(),
    customer_name_phonetic: SOLE_PROPRIETOR_INPUT_SHAPE.customer_name_phonetic.optional(),
    trade_partner_type: zTradePartnerTypes.optional(),
    version: zVersion,
  })
  .strict();

const CONTACT_FIELDS_SHAPE = {
  last_name: zRequiredText(50, 'MSG-V-078'),
  first_name: zRequiredText(50, 'MSG-V-079'),
  job_title: zShortText(100).optional(),
  department_1: zShortText(100).optional(),
  department_2: zShortText(100).optional(),
  phone_number: zNullable(zPhone).optional(),
  email: zNullable(zEmail).optional(),
};

/** API-CCON-02 企業担当者の登録 */
export const CustomerContactCreateSchema = z.object({ customer_code: zCode, ...CONTACT_FIELDS_SHAPE }).strict();

/** API-CCON-04 企業担当者の更新（名刺の付け替えを含む） */
export const CustomerContactUpdateSchema = z
  .object({
    ...CONTACT_FIELDS_SHAPE,
    last_name: CONTACT_FIELDS_SHAPE.last_name.optional(),
    first_name: CONTACT_FIELDS_SHAPE.first_name.optional(),
    business_card_front_attachment_code: zNullable(zSystemCode).optional(),
    business_card_back_attachment_code: zNullable(zSystemCode).optional(),
    version: zVersion,
  })
  .strict();

/** API-CCON-06 主担当の設定・解除（null で解除。version は customers の version） */
export const PrimaryCustomerContactPutSchema = z
  .object({ customer_contact_code: zSystemCode.nullable(), version: zVersion })
  .strict();

/** 削除のクエリ `?version=`（02 2.7） */
export const DeleteVersionQuerySchema = z.object({
  version: z.coerce.number({ message: 'MSG-V-020' }).int('MSG-V-020').min(1, 'MSG-V-020'),
});

// ───────── 応答の型 ─────────

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const CustomerContactSchema = z
  .object({
    customer_contact_code: z.string(),
    customer_code: z.string(),
    last_name: z.string(),
    first_name: z.string(),
    job_title: z.string().nullable(),
    department_1: z.string().nullable(),
    department_2: z.string().nullable(),
    phone_number: z.string().nullable(),
    email: z.string().nullable(),
    business_card_front_attachment_code: z.string().nullable(),
    business_card_back_attachment_code: z.string().nullable(),
    is_primary: z.boolean(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

const CUSTOMER_COMMON_RESPONSE_SHAPE = {
  customer_code: z.string(),
  customer_type: z.enum(CUSTOMER_TYPE_VALUES),
  customer_name: z.string(),
  customer_name_phonetic: z.string(),
  postal_code: z.string().nullable(),
  address: z.string().nullable(),
  invoice_number: z.string().nullable(),
  trade_partner_type: z.array(z.enum(TRADE_PARTNER_TYPE_VALUES)),
  sales_user_code: z.string().nullable(),
  sales_user_name: z.string().nullable(),
  /** 営業担当の利用者が削除済み（02 2.9「<参照先>_deleted」） */
  sales_user_deleted: z.boolean(),
  tag_code: z.enum(TAG_CODE_VALUES).nullable(),
  tag_name: z.string().nullable(),
  tag_color: z.string().nullable(),
  bank_name: z.string().nullable(),
  branch_name: z.string().nullable(),
  bank_code: z.string().nullable(),
  branch_code: z.string().nullable(),
  bank_account_type: z.enum(BANK_ACCOUNT_TYPE_VALUES).nullable(),
  /** 詳細だけ。復号して全桁（02 5.3.3・要件書 F-03） */
  account_number: z.string().nullable(),
  account_holder_name: z.string().nullable(),
  account_holder_name_phonetic: z.string().nullable(),
};

export const CompanySchema = z
  .object({
    ...CUSTOMER_COMMON_RESPONSE_SHAPE,
    representative_name: z.string().nullable(),
    website_url: z.string().nullable(),
    corporate_number: z.string().nullable(),
    trade_type: z.array(z.enum(TRADE_TYPE_VALUES)),
    primary_customer_contact_code: z.string().nullable(),
    primary_customer_contact: CustomerContactSchema.nullable(),
  })
  .merge(CommonAttributesSchema);

export const SoleProprietorSchema = z
  .object({
    ...CUSTOMER_COMMON_RESPONSE_SHAPE,
    phone_number: z.string().nullable(),
    email: z.string().nullable(),
    my_number_last_four_digits: z.string().nullable(),
    has_my_number: z.boolean(),
  })
  .merge(CommonAttributesSchema);

const SUMMARY_TAG_SHAPE = {
  tag_code: z.enum(TAG_CODE_VALUES).nullable(),
  tag_name: z.string().nullable(),
  tag_color: z.string().nullable(),
  sales_user_code: z.string().nullable(),
  sales_user_name: z.string().nullable(),
};

/** API-COMP-01 の 1 行（CustomerSummary） */
export const CustomerSummarySchema = z
  .object({
    customer_code: z.string(),
    customer_type: z.enum(CUSTOMER_TYPE_VALUES),
    customer_name: z.string(),
    customer_name_phonetic: z.string(),
    trade_partner_type: z.array(z.enum(TRADE_PARTNER_TYPE_VALUES)),
    primary_customer_contact_code: z.string().nullable(),
    primary_customer_contact_name: z.string().nullable(),
    email: z.string().nullable(),
    phone_number: z.string().nullable(),
    ...SUMMARY_TAG_SHAPE,
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

/** API-COMP-03 の 1 行（CompanySummary） */
export const CompanySummarySchema = z
  .object({
    customer_code: z.string(),
    customer_name: z.string(),
    customer_name_phonetic: z.string(),
    trade_type: z.array(z.enum(TRADE_TYPE_VALUES)),
    trade_partner_type: z.array(z.enum(TRADE_PARTNER_TYPE_VALUES)),
    primary_customer_contact_code: z.string().nullable(),
    primary_customer_contact_name: z.string().nullable(),
    primary_customer_contact_email: z.string().nullable(),
    primary_customer_contact_phone_number: z.string().nullable(),
    ...SUMMARY_TAG_SHAPE,
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

/** API-SOLE-01 の 1 行（SoleProprietorSummary） */
export const SoleProprietorSummarySchema = z
  .object({
    customer_code: z.string(),
    customer_name: z.string(),
    customer_name_phonetic: z.string(),
    phone_number: z.string().nullable(),
    email: z.string().nullable(),
    trade_partner_type: z.array(z.enum(TRADE_PARTNER_TYPE_VALUES)),
    ...SUMMARY_TAG_SHAPE,
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

/** API-COMP-10 の 1 件 */
export const TagSchema = z.object({
  tag_code: z.enum(TAG_CODE_VALUES),
  tag_name: z.string(),
  tag_color: z.string(),
  display_order: z.number().int(),
});

/** 削除の応答（02 2.9。200 は job_code=null、202 は deleted_counts に親だけ） */
export const SoftDeleteResponseSchema = z.object({
  target_entity_type: z.string(),
  target_entity_key: z.string(),
  deleted_at: z.string(),
  delete_batch_code: z.string(),
  deleted_counts: z.record(z.number().int()),
  job_code: z.string().nullable(),
});

/** 復元の応答（02 2.9） */
export const RestoreResponseSchema = z.object({
  target_entity_type: z.string(),
  target_entity_key: z.string(),
  delete_batch_code: z.string(),
  restored_counts: z.record(z.number().int()),
  job_code: z.string().nullable(),
});

/** 一括削除の応答（02 2.9） */
export const BulkDeleteResponseSchema = z.object({
  succeeded: z.array(z.object({ code: z.string(), delete_batch_code: z.string() })),
  failed: z.array(z.object({ code: z.string(), error_code: z.string() })),
  success_count: z.number().int(),
  fail_count: z.number().int(),
});

export const PrimaryCustomerContactResponseSchema = z.object({
  customer_code: z.string(),
  primary_customer_contact_code: z.string().nullable(),
  version: z.number().int(),
});

// ───────── 一覧のクエリ（02 2.3.2） ─────────

export const CUSTOMER_LIST_SORT_VALUES = ['customer_code', 'customer_name', 'created_at'] as const;
export const CUSTOMER_CONTACT_LIST_SORT_VALUES = ['last_name', 'created_at'] as const;

const CUSTOMER_FILTER_SHAPE = {
  tag_code: zEnum(TAG_CODE_VALUES).optional(),
  sales_user_code: zSystemCode.optional(),
  trade_partner_type: zEnum(TRADE_PARTNER_TYPE_VALUES).optional(),
};

/** API-COMP-01 */
export const CustomerListQuerySchema = buildListQuerySchema(CUSTOMER_LIST_SORT_VALUES, 'customer_code').extend({
  customer_type: zEnum(CUSTOMER_TYPE_VALUES).optional(),
  ...CUSTOMER_FILTER_SHAPE,
});

/** API-COMP-03 */
export const CompanyListQuerySchema = buildListQuerySchema(CUSTOMER_LIST_SORT_VALUES, 'customer_code').extend({
  ...CUSTOMER_FILTER_SHAPE,
  trade_type: zEnum(TRADE_TYPE_VALUES).optional(),
});

/** API-SOLE-01 */
export const SoleProprietorListQuerySchema = buildListQuerySchema(CUSTOMER_LIST_SORT_VALUES, 'customer_code').extend(
  CUSTOMER_FILTER_SHAPE,
);

/** API-CCON-01（customer_code 必須） */
export const CustomerContactListQuerySchema = buildListQuerySchema(
  CUSTOMER_CONTACT_LIST_SORT_VALUES,
  'created_at',
).extend({ customer_code: zCode });

export type PrimaryContactInput = z.infer<typeof PrimaryContactInputSchema>;
export type CompanyCreateInput = z.infer<typeof CompanyCreateSchema>;
export type CompanyUpdateInput = z.infer<typeof CompanyUpdateSchema>;
export type SoleProprietorCreateInput = z.infer<typeof SoleProprietorCreateSchema>;
export type SoleProprietorUpdateInput = z.infer<typeof SoleProprietorUpdateSchema>;
export type CustomerContactCreateInput = z.infer<typeof CustomerContactCreateSchema>;
export type CustomerContactUpdateInput = z.infer<typeof CustomerContactUpdateSchema>;
export type PrimaryCustomerContactPutInput = z.infer<typeof PrimaryCustomerContactPutSchema>;
export type CustomerContact = z.infer<typeof CustomerContactSchema>;
export type Company = z.infer<typeof CompanySchema>;
export type SoleProprietor = z.infer<typeof SoleProprietorSchema>;
export type CustomerSummary = z.infer<typeof CustomerSummarySchema>;
export type CompanySummary = z.infer<typeof CompanySummarySchema>;
export type SoleProprietorSummary = z.infer<typeof SoleProprietorSummarySchema>;
export type Tag = z.infer<typeof TagSchema>;
export type SoftDeleteResponse = z.infer<typeof SoftDeleteResponseSchema>;
export type RestoreResponse = z.infer<typeof RestoreResponseSchema>;
export type BulkDeleteResponse = z.infer<typeof BulkDeleteResponseSchema>;
export type CustomerListQuery = z.infer<typeof CustomerListQuerySchema>;
export type CompanyListQuery = z.infer<typeof CompanyListQuerySchema>;
export type SoleProprietorListQuery = z.infer<typeof SoleProprietorListQuerySchema>;
export type CustomerContactListQuery = z.infer<typeof CustomerContactListQuerySchema>;
